import React from 'react';
import styled from 'styled-components';
import { Theme } from '../../theme';

const BottomNavContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${props => props.theme.surface};
  border-top: 1px solid ${props => props.theme.border};
  padding: 8px 16px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 60px;
  box-sizing: border-box;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: ${props => props.theme.surface}f0; /* Semi-transparent */
  z-index: 1000;
  
  /* Safe area support for iPhone notch */
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  
  /* PWA mode - increase height and padding */
  @media (display-mode: standalone) {
    height: 76px;
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom) + 8px);
  }
  
  /* Shadow for separation */
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
`;

const NavButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
  position: relative;
  
  /* Active state */
  background: ${props => props.$active ? props.theme.primary + '20' : 'transparent'};
  
  &:hover {
    background: ${props => props.$active ? props.theme.primary + '30' : props.theme.hoverBackground};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  /* Prevent text selection */
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

const NavIcon = styled.div<{ $active?: boolean }>`
  font-size: 18px;
  color: ${props => props.$active ? props.theme.primary : props.theme.text};
  transition: color 0.2s ease;
  line-height: 1;
`;

const NavLabel = styled.span<{ $active?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${props => props.$active ? props.theme.primary : props.theme.subText};
  transition: color 0.2s ease;
  text-align: center;
  line-height: 1;
`;

const FilterBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background: ${props => props.theme.primary};
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 6px;
  min-width: 12px;
  text-align: center;
  line-height: 1;
`;

export type FilterStatus = 'all' | 'success' | 'miss' | 'pending' | 'updated' | 'bet' | 'sentiment';

interface MobileBottomNavProps {
  theme: Theme;
  onMenuClick: () => void;
  onResetChart: () => void;
  statusFilter: FilterStatus;
  onStatusFilterChange: (filter: FilterStatus) => void;
  filteredCount?: number;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  theme,
  onMenuClick,
  onResetChart,
  statusFilter,
  onStatusFilterChange,
  filteredCount
}) => {
  const getFilterIcon = (filter: FilterStatus) => {
    switch (filter) {
      case 'success': return '✓';
      case 'miss': return '✗';
      case 'pending': return '⏳';
      case 'updated': return '🔄';
      case 'bet': return '🎲';
      case 'sentiment': return '💬';
      default: return '🎯';
    }
  };

  const getFilterLabel = (filter: FilterStatus) => {
    switch (filter) {
      case 'success': return 'Success';
      case 'miss': return 'Miss';
      case 'pending': return 'Pending';
      case 'updated': return 'Updated';
      case 'bet': return 'Bet';
      case 'sentiment': return 'Talk';
      default: return 'All';
    }
  };

  const cycleFilter = () => {
    const filters: FilterStatus[] = ['all', 'success', 'miss', 'pending', 'updated', 'bet', 'sentiment'];
    const currentIndex = filters.indexOf(statusFilter);
    const nextIndex = (currentIndex + 1) % filters.length;
    onStatusFilterChange(filters[nextIndex]);
  };

  return (
    <BottomNavContainer theme={theme}>
      <NavButton 
        onClick={onMenuClick}
        theme={theme}
        title="Menu"
      >
        <NavIcon theme={theme}>☰</NavIcon>
        <NavLabel theme={theme}>Menu</NavLabel>
      </NavButton>

      <NavButton 
        onClick={onResetChart}
        theme={theme}
        title="Reset Chart View"
      >
        <NavIcon theme={theme}>🔄</NavIcon>
        <NavLabel theme={theme}>Reset</NavLabel>
      </NavButton>

      <NavButton 
        onClick={cycleFilter}
        theme={theme}
        $active={statusFilter !== 'all'}
        title={`Filter: ${getFilterLabel(statusFilter)}`}
      >
        <NavIcon theme={theme} $active={statusFilter !== 'all'}>
          {getFilterIcon(statusFilter)}
        </NavIcon>
        <NavLabel theme={theme} $active={statusFilter !== 'all'}>
          {getFilterLabel(statusFilter)}
        </NavLabel>
        {filteredCount !== undefined && statusFilter !== 'all' && (
          <FilterBadge theme={theme}>{filteredCount}</FilterBadge>
        )}
      </NavButton>
    </BottomNavContainer>
  );
};

export default MobileBottomNav;
